<script>
  import {onMount, tick} from 'svelte';
  import {_} from '../locales';
  import Section from './Section.svelte';
  import Button from './Button.svelte';
  import DropArea from './DropArea.svelte';
  import ComplexMessage from './ComplexMessage.svelte';
  import ImportingProject from './ImportProject.svelte';
  import writablePersistentStore from './persistent-store';
  import {progress, currentTask} from './stores';
  import {UserError} from '../common/errors';
  import loadProject from '../packager/load-project';
  import {extractProjectId} from './url-utils';
  import Task from './task';
  import importExternalProject from './import-external-project';
  import {fetchCreationDetail, fetchCreationReleases} from '../common/gandi/api';

  const type = writablePersistentStore('SelectProject.type', 'file');
  const projectId = writablePersistentStore('SelectProject.id', '');
  const creationOid = writablePersistentStore('SelectProject.creationOid', '');

  let creationDetail = null;
  let creationReleases = [];
  let selectedRelease = null;
  let currentPage = 1;
  let hasMore = false;
  let isLoadingReleases = false;
  let isCreationDataLoaded = false;

  const projectIdInURL = /^#\d+$/.test(location.hash) ? location.hash.substring(1) : null;
  if (projectIdInURL) {
    $type = 'creationId';
    $projectId = projectIdInURL;
  }

  let isImportingProject = false;
  importExternalProject({
    onStartImporting: () => {
      isImportingProject = true;
    },
    onCancelImporting: () => {
      isImportingProject = false;
    },
    onFinishImporting: (files) => {
      if (!isImportingProject) {
        // Import was cancelled.
        return;
      }
      $type = 'file';
      isImportingProject = false;
      fileInputElement.files = files;
      setFiles(files);
    }
  });

  export let projectData = null;
  const resetProjectAndCancelTask = () => {
    projectData = null;
    currentTask.abort();
    creationDetail = null;
    creationReleases = [];
    selectedRelease = null;
    currentPage = 1;
    isCreationDataLoaded = false;
    hasMore = false;
    isLoadingReleases = false;
  };

  // Reset project when input changes
  $: $projectId, $type, $creationOid, resetProjectAndCancelTask();

  // just incase some non-number string was stored from older versions
  $projectId = extractProjectId($projectId);

  const submitOnEnter = (e) => {
    if (e.key === 'Enter') {
      if ($type === 'creationId') {
        loadCreationData();
      } else {
        load();
      }
    }
  };
  const loadCreationData = async () => {
    if (!$creationOid) {
      alert($_('select.invalidCreationOid'));
      return;
    }

    isLoadingReleases = true;
    isCreationDataLoaded = false;
    creationReleases = [];
    selectedRelease = null;
    currentPage = 1;
    hasMore = true;

    try {
      const detail = await fetchCreationDetail($creationOid);
      if (!detail) {
        throw new UserError($_('select.fetchCreationFailed'));
      }
      creationDetail = detail;

      const firstPageData = await fetchCreationReleases(
        $creationOid,
        currentPage,
        true
      );

      if (firstPageData && firstPageData) {
        creationReleases = creationDetail.creationReleaseList
          ? [...creationDetail.creationReleaseList]
          : [...firstPageData];

        hasMore = creationReleases.length >= 20;
        currentPage++;
        isCreationDataLoaded = true;
        if (creationReleases.length > 0) {
          selectedRelease = creationReleases[0];
        }
      } else {
        throw new UserError($_('select.noReleasesFound'));
      }
    } catch (error) {
      console.error('加载版本列表失败:', error);
      alert(error.message || $_('select.fetchReleasesFailed'));
    } finally {
      isLoadingReleases = false;
    }
  };
  const loadMoreReleases = async () => {
    if (isLoadingReleases || !hasMore) return;

    isLoadingReleases = true;
    try {
      const releasesData = await fetchCreationReleases(
        $creationOid,
        currentPage,
        true
      );

      if (releasesData && releasesData && releasesData.length > 0) {
        creationReleases = [...creationReleases, ...releasesData];
        hasMore = releasesData.length === 20;
        currentPage++;
      } else {
        hasMore = false;
      }
    } catch (error) {
      console.error('加载更多版本失败:', error);
      alert($_('select.loadMoreFailed'));
    } finally {
      isLoadingReleases = false;
    }
  };
  const selectRelease = (release) => {
    selectedRelease = release;
  };

  let fileInputElement;

  const copyFileList = (files) => {
    const transfer = new DataTransfer();
    for (const file of files) {
      transfer.items.add(file);
    }
    return transfer.files;
  };

  // This is used to remember files between reloads in some browsers (currently only Firefox)
  // This element is defined in the HTML of template.ejs because some browsers won't
  // autocomplete file inputs generated by JS.
  const inputForRememberingProjectFile = document.querySelector('.input-for-remembering-project-file');
  if (inputForRememberingProjectFile) {
    // Check for autocompleted files after mount so that fileInputElement is defined.
    onMount(() => {
      const storedFiles = inputForRememberingProjectFile.files;
      if (storedFiles.length) {
        fileInputElement.files = copyFileList(storedFiles);
      }
    });
  }

  const setFiles = (files) => {
    resetProjectAndCancelTask();
    if (fileInputElement.files !== files) {
      fileInputElement.files = files;
    }
    if (inputForRememberingProjectFile) {
      inputForRememberingProjectFile.files = copyFileList(files);
    }
    if (files.length && $type === 'file') {
      // if $type was updated before calling this function, wait for the current task to get
      // cancelled before we start the next one
      tick().then(load);
    }
  };
  const handleDrop = ({detail: dataTransfer}) => {
    const name = dataTransfer.files[0].name;
    if (name.endsWith('.sb') || name.endsWith('.sb2') || name.endsWith('.sb3')) {
      $type = 'file';
      setFiles(dataTransfer.files);
    }
  };
  const handleFileInputChange = (e) => {
    setFiles(e.target.files);
  };

  const internalLoad = async (task) => {
    let uniqueId = '';
    let id = null;
    let projectTitle = '';
    let project;

    const progressCallback = (type, a, b) => {
      if (type === 'fetch') {
        task.setProgress(a);
      } else if (type === 'assets') {
        task.setProgressText(
          $_('progress.loadingAssets')
            .replace('{complete}', a)
            .replace('{total}', b)
        );
        task.setProgress(a / b);
      } else if (type === 'compress') {
        task.setProgressText($_('progress.compressingProject'));
        task.setProgress(a);
      }
    };

    if ($type === 'creationId') {
      if (!creationDetail || !selectedRelease) {
        throw new UserError($_('select.noReleaseSelected'));
      }

      id = selectedRelease.oid;
      uniqueId = `%${$creationOid}-${selectedRelease.oid}`;
      projectTitle = creationDetail.title || selectedRelease.version;

      task.setProgressText($_('progress.loadingProjectData'));
      const { promise: loadProjectPromise, terminate } =
        await loadProject.fromCCWSb3URL(
          selectedRelease.projectLink,
          progressCallback
        );
      task.whenAbort(terminate);
      project = await loadProjectPromise;

      return {
        projectId: $creationOid,
        uniqueId,
        title: projectTitle,
        project,
        releaseInfo: {
          title: creationDetail.title,
          projectLink: selectedRelease.projectLink,
          version: selectedRelease.version,
          customVersion: selectedRelease.customVersion,
          tags: selectedRelease.tags,
          oid: selectedRelease.oid,
        },
      };
    } else if ($type === 'file') {
      const files = fileInputElement.files;
      const file = files && files[0];
      if (!file) {
        throw new UserError($_('select.noFileSelected'));
      }
      uniqueId = `@${file.name}`;
      projectTitle = file.name;
      task.setProgressText($_('progress.compressingProject'));
      project = await (await loadProject.fromFile(file, progressCallback)).promise;
    } else {
      throw new Error('Unknown type');
    }

    return {
      projectId: id,
      uniqueId,
      title: projectTitle,
      project,
    };
  };
  const load = async () => {
    if ($type !== 'creationId') resetProjectAndCancelTask();
    const task = new Task();
    if ($type === 'creationId') {
      try {
        if (!isCreationDataLoaded || !selectedRelease) {
          throw new UserError($_('select.pleaseLoadReleasesFirst'));
        }
      } catch (error) {
        console.error('加载创作数据失败:', error);
        task.error(error);
        return;
      }
    }
    projectData = await task.do(internalLoad(task));
    task.done();
  };
</script>

{#if isImportingProject}
  <ImportingProject on:cancel={() => {
    isImportingProject = false;
  }} />
{/if}

<DropArea on:drop={handleDrop}>
  <Section accent="#4C97FF">
    <h2>{$_('select.select')}</h2>
    <p>{$_('select.selectHelp')}</p>

    <div class="options">
      <!-- TurboWarp Desktop looks for the file-input-option class for special handling, so be careful when modifying this. -->
      <div class="option file-input-option">
        <label>
          <input type="radio" name="project-type" bind:group={$type} value="file">
          {$_('select.file')}
        </label>
        <input hidden={$type !== "file"} on:change={handleFileInputChange} bind:this={fileInputElement} type="file" accept=".sb,.sb2,.sb3">
      </div>
      <div class="option">
        <label>
          <input type="radio" name="project-type" bind:group={$type} value="creationId">
          {$_('select.creationOid')}
        </label>
        {#if $type === "creationId"}
          <input 
            type="text" 
            bind:value={$creationOid} 
            spellcheck="false" 
            placeholder={$_('select.creationOidPlaceholder')} 
            on:keypress={submitOnEnter}
          >
          <button on:click={() => submitOnEnter({ key: 'Enter' })}>{$_('select.creationOidSearch')}</button>
        {/if}
      </div>
    </div>
    {#if $type === 'creationId' && isCreationDataLoaded}
      <div class="releases-container">
        {#if creationReleases.length === 0}
          <p>{$_('select.noReleasesFound')}</p>
        {:else}
          {#each creationReleases as release, index (release.oid)}
            <!-- svelte-ignore a11y-no-noninteractive-tabindex -->
            <div
              class="release-item {selectedRelease &&
              selectedRelease.oid === release.oid
                ? 'selected'
                : ''}"
              on:click={() => selectRelease(release)}
              on:keydown={(e) => e.key === 'Enter' && selectRelease(release)}
              tabindex="0"
            >
              <div class="release-header">
                <span class="release-version">v{release.customVersion || release.version} {release.description}</span>
                <span class="release-date">
                  {new Date(release.submittedAt).toLocaleString()}
                </span>
              </div>
              <div class="release-tags">
                {#if index === 0}
                  <span class="tag latest">{$_('select.latest')}</span>
                {/if}
                {#each release.tags as tag}
                  <span class="tag">{tag}</span>
                {/each}
              </div>
            </div>
          {/each}

          {#if hasMore}
            <div class="load-more">
              <Button
                text={$_('select.loadMore')}
                on:click={loadMoreReleases}
                disabled={isLoadingReleases}
                small
              />
              {#if isLoadingReleases}
                <span>{$_('select.loading')}...</span>
              {/if}
            </div>
          {/if}
        {/if}
      </div>
    {/if}
    {#if $type === 'creationId'}
      <p>
        {$_('select.unsharedProjects')}
      </p>
      <p>
        {$_('select.unsharedProjectsWorkaround')}
      </p>
      <p>
        <ComplexMessage
          message={$_('select.unsharedProjectsMore')}
          values={{
            link: {
              text: 'https://docs.turbowarp.org/unshared-projects',
              href: 'https://docs.turbowarp.org/unshared-projects',
              newTab: true
            }
          }}
        />
      </p>
    {/if}

    <Button
      on:click={load}
      text={$_('select.loadProject')}
      disabled={($type === 'creationId' &&
        (!isCreationDataLoaded || !selectedRelease)) ||
        isLoadingReleases}
    />
  </Section>
</DropArea>

{#if !$progress.visible && !projectData}
  <Section caption>
    <p>{$_('select.loadToContinue')}</p>
  </Section>
{/if}

<style>
  input[type='text'] {
    max-width: 300px;
    flex-grow: 1;
  }
  .options {
    margin: 12px 0;
  }
  .option {
    min-height: 25px;
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    margin-bottom: 8px;
    gap: 8px;
  }
  input[type='text'],
  input[type='file'] {
    margin-left: 4px;
  }
  .releases-container {
    margin: 12px 0;
    padding: 12px;
    border: 1px solid #e0e0e0;
    border-radius: 4px;
    max-height: 300px;
    overflow-y: auto;
  }
  .release-item {
    padding: 8px;
    margin-bottom: 8px;
    border-radius: 4px;
    cursor: pointer;
    border: 1px solid transparent;
  }
  .release-item:hover {
    background-color: #f5f5f5;
  }
  .release-item.selected {
    border-color: #4c97ff;
    background-color: #e8f0fe;
  }
  .release-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }
  .release-version {
    font-weight: bold;
  }
  .release-date {
    font-size: 0.8em;
    color: #666;
  }
  .release-tags {
    display: flex;
    gap: 4px;
    margin-top: 4px;
  }
  .tag {
    font-size: 0.7em;
    padding: 2px 6px;
    border-radius: 12px;
    background-color: #e0e0e0;
  }
  .tag.latest {
    background-color: #4caf50;
    color: white;
  }
  .load-more {
    margin-top: 12px;
    text-align: center;
  }

  :global([theme='dark']) .releases-container {
    border-color: #333;
    background-color: #1a1a1a;
  }
  :global([theme='dark']) .release-item {
    background-color: #2d2d2d;
    color: #f0f0f0;
  }
  :global([theme='dark']) .release-item:hover {
    background-color: #3d3d3d;
  }
  :global([theme='dark']) .release-item.selected {
    border-color: #4c97ff;
    background-color: #2a3b5c;
  }
  :global([theme='dark']) .release-date {
    color: #bbb;
  }
  :global([theme='dark']) .tag {
    background-color: #444;
    color: #ddd;
  }
  :global([theme='dark']) .tag.latest {
    background-color: #45a049;
  }
</style>
